import './Users.css';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TOTAL_VOICES,
  BY_PERSPECTIVE,
  BY_REGION,
  BY_AGE,
  countFor,
} from './usersData';

function fmt(n) {
  return n.toLocaleString('en-US');
}

// ── Stacked proportion bar (signature visualization for perspective) ──
function StackedBar({ segments }) {
  return (
    <div className="usr-stack" role="img" aria-label="Composition by perspective">
      {segments.map((s) => (
        <span
          key={s.id}
          className="usr-stack-seg"
          style={{ width: `${s.pct}%`, background: s.color }}
        />
      ))}
    </div>
  );
}

// ── Slim monochrome bar row (region / age) ──
function BarRow({ label, detail, pct, max }) {
  return (
    <div className="usr-bar-row">
      <div className="usr-bar-meta">
        <span className="usr-bar-label">{label}</span>
        {detail && <span className="usr-bar-detail">{detail}</span>}
      </div>
      <div className="usr-bar-track">
        <span className="usr-bar-fill" style={{ width: `${(pct / max) * 100}%` }} />
      </div>
      <span className="usr-bar-pct">{pct}%</span>
    </div>
  );
}

export default function Users() {
  const history     = useHistory();
  const { t }       = useTranslation();

  const regionMax = Math.max(...BY_REGION.map((r) => r.pct));
  const ageMax    = Math.max(...BY_AGE.map((a) => a.pct));

  return (
    <div className="usr-root">
      <div className="usr-scroll">

        <button className="usr-back" onClick={() => history.goBack()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3L5 8l5 5" />
          </svg>
          {t('td.back')}
        </button>

        {/* Intro */}
        <div className="usr-intro">
          <span className="usr-eyebrow">Munich Voices · User base</span>
          <h1 className="usr-title">Who's behind the Munich Voices</h1>
          <p className="usr-lead">
            Every measurement draws on the same community - from a verified core of
            delegates and experts to the broad public following along. Here's how it's
            composed.
          </p>
        </div>

        {/* Hero stat */}
        <div className="usr-hero">
          <span className="usr-hero-num">{fmt(TOTAL_VOICES)}</span>
          <div className="usr-hero-aside">
            <span className="usr-hero-unit">verified voices</span>
            <p className="usr-hero-body">measured across three events in the series.</p>
          </div>
        </div>

        {/* ── By perspective ── */}
        <section className="usr-section">
          <span className="usr-section-label">By perspective</span>
          <StackedBar segments={BY_PERSPECTIVE} />
          <div className="usr-legend">
            {BY_PERSPECTIVE.map((s) => (
              <div key={s.id} className="usr-legend-row">
                <span className="usr-legend-dot" style={{ background: s.color }} />
                <div className="usr-legend-text">
                  <span className="usr-legend-name">{s.label}</span>
                  <span className="usr-legend-blurb">{s.blurb}</span>
                </div>
                <div className="usr-legend-val">
                  <span className="usr-legend-pct">{s.pct}%</span>
                  <span className="usr-legend-count">{fmt(countFor(s.pct))}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── By region ── */}
        <section className="usr-section">
          <span className="usr-section-label">By region</span>
          <div className="usr-bars">
            {BY_REGION.map((r) => (
              <BarRow key={r.id} label={r.label} detail={r.detail} pct={r.pct} max={regionMax} />
            ))}
          </div>
        </section>

        {/* ── By age ── */}
        <section className="usr-section">
          <span className="usr-section-label">By age</span>
          <div className="usr-bars">
            {BY_AGE.map((a) => (
              <BarRow key={a.id} label={a.label} pct={a.pct} max={ageMax} />
            ))}
          </div>
        </section>

        <p className="usr-foot">
          Figures are illustrative demo data, modelled on the perspective cohorts used
          throughout Takes.
        </p>
        <p className="usr-copyright">Prototype by Ferdinand Riedl · riedlsoftware.com</p>

      </div>
    </div>
  );
}
